/* Smart Film PDP v3 for Shopify: chapters, loops and the small interactions that answer one objection each.
   Port of pdp/dist/dtc/v3.js. sf-app.js keeps the offer, pricing, cart and FAQ; this file never changes prices or the
   offer block, it only reads them. Every part is optional: a section removed in the theme editor simply finds nothing.
   Classes get the theme prefix (data-class-prefix on the script tag, "sf-"); ids of this file all start with sf-. */
(() => {
  'use strict';
  const P = document.currentScript?.dataset.classPrefix ?? 'sf-';
  const q = selector => selector.replace(/\.(?=[a-z])/g, '.' + P);
  const cls = name => P + name;
  const $ = id => document.getElementById(id);
  const $$ = (selector, scope = document) => [...scope.querySelectorAll(selector)];
  const root = document.documentElement;
  root.classList.add('sf-js');
  const reduced = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  const live = text => { const node = $('live-status'); if (node) node.textContent = text; };
  let config = {};
  try { config = JSON.parse($('sf-config')?.textContent || '{}'); } catch (_) { /* the product section reports it */ }
  const currency = config.currency || 'USD';
  const money = cents => new Intl.NumberFormat(config.locale || 'en-US', { style: 'currency', currency, maximumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);
  const usd = n => '$' + Math.round(n).toLocaleString('en-US');

  /* ---------- Motion: page-wide pause (WCAG 2.2.2) ---------- */
  let paused = false;
  try { paused = localStorage.getItem('sf-motion-paused') === '1'; } catch (_) { /* storage optional */ }
  const motionAllowed = () => !paused && !reduced();
  const toggles = $$('#sf-motion-toggle, [data-motion-toggle]');
  function setPaused(next) {
    paused = next;
    root.dataset.motion = paused ? 'paused' : 'on';
    toggles.forEach(btn => {
      const label = (paused ? btn.dataset.playLabel : btn.dataset.pauseLabel) || (paused ? 'Play motion' : 'Pause motion');
      btn.setAttribute('aria-pressed', String(paused));
      btn.setAttribute('aria-label', label);
      const text = btn.querySelector(q('.mt-label'));
      if (text) text.textContent = label;
    });
    try { localStorage.setItem('sf-motion-paused', paused ? '1' : '0'); } catch (_) { /* optional */ }
    loops.forEach(v => (paused ? pause(v) : (v.dataset.inview === '1' && play(v))));
    hero.sync();
  }
  toggles.forEach(b => b.addEventListener('click', () => setPaused(!paused)));

  /* ---------- Loops: lazy, muted, inline, at most three playing ---------- */
  const loops = $$(q('video.lv'));
  const playing = new Set();
  function play(v) {
    if (!motionAllowed() || v.dataset.hold === '1') return;
    if (!playing.has(v) && playing.size >= 3) return;
    if (!v.getAttribute('src') && v.dataset.src) { v.src = v.dataset.src; v.load(); }
    const p = v.play();
    playing.add(v);
    if (p && p.catch) p.catch(() => playing.delete(v));
  }
  function pause(v) { v.pause(); playing.delete(v); }
  loops.forEach(v => {
    v.muted = true;
    v.setAttribute('muted', '');
    v.addEventListener('webkitbeginfullscreen', () => { try { v.webkitExitFullscreen(); } catch (_) { /* iOS only */ } });
  });
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(entries => entries.forEach(e => {
      const v = e.target;
      v.dataset.inview = e.isIntersecting ? '1' : '0';
      if (e.isIntersecting) play(v); else pause(v);
    }), { rootMargin: '150px 0px' });
    loops.forEach(v => io.observe(v));
  }
  document.addEventListener('visibilitychange', () => { if (document.hidden) loops.forEach(pause); });

  /* ---------- Gallery slide 1: the filmed switch (loop L01) until the visitor takes the remote ----------
     Only when a video is picked on the live-switch block (data-loop-src); otherwise the photo switch stays. */
  const hero = (() => {
    const scene = $('hero-scene');
    const src = scene?.dataset.loopSrc, greySrc = scene?.dataset.greySrc;
    // The loop is 480p: sharp enough on a phone, soft at desktop size, where the crisp photo demo stays instead.
    const wide = typeof matchMedia === 'function' && matchMedia('(min-width: 1024px)').matches;
    if (!scene || !src || wide) return { sync() {} };
    const v = document.createElement('video');
    Object.assign(v, { muted: true, loop: true, playsInline: true, preload: 'none' });
    v.className = cls('hero-loop');
    ['muted', 'playsinline', 'webkit-playsinline', 'disablepictureinpicture', 'disableremoteplayback'].forEach(a => v.setAttribute(a, ''));
    v.setAttribute('aria-hidden', 'true');
    v.poster = scene.dataset.loopPoster || '';
    let touched = false, started = false, ready = false;
    const start = () => {
      if (started || touched || !motionAllowed()) return;
      started = true;
      v.src = src;
      scene.prepend(v); // first in the slide's media order; CSS layers it above the photos
      root.dataset.heroVideo = 'on';
      v.play().catch(() => { root.dataset.heroVideo = 'off'; v.remove(); started = false; });
    };
    let onScreen = true;
    // Grey swatch: the slide plays the grey film's switch (the "grey loop" picked on the live-switch block); without one the CSS tint stays.
    let grey = null;
    const greyOff = () => { if (grey) { grey.pause(); grey.remove(); grey = null; } };
    const greyOn = () => {
      if (!greySrc || !motionAllowed()) return;
      grey = v.cloneNode();
      grey.src = greySrc;
      grey.poster = scene.dataset.greyPoster || '';
      scene.prepend(grey);
      root.dataset.heroVideo = 'on';
      // The swatches sit below the slide on phones, so it often starts off screen: the observer plays it on the way back up.
      if (onScreen) grey.play().catch(e => { if (e?.name !== 'AbortError') { greyOff(); root.dataset.heroVideo = 'off'; } });
    };
    const stop = () => { touched = true; v.pause(); v.remove(); greyOff(); root.dataset.heroVideo = 'off'; };
    scene.addEventListener('click', stop, { capture: true });
    // The remote hands the slide back to the visitor; the swatches do too, except that grey shows its own loop.
    $$('[data-scene-mode]').forEach(b => b.addEventListener('click', stop, { capture: true }));
    $$(q('.swatches [data-colour]')).forEach(b => b.addEventListener('click', () => {
      stop();
      // owner 30/10: a colour tap shows that colour frosted at once (the still), not a loop that starts on clear glass
    }, { capture: true }));
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(([e]) => {
        onScreen = e.isIntersecting;
        if (grey) { if (onScreen && motionAllowed()) grey.play().catch(() => {}); else grey.pause(); }
        if (!started || touched) return;
        if (onScreen && motionAllowed()) v.play().catch(() => {}); else v.pause();
      }, { threshold: 0.25 }).observe(scene);
    }
    // The poster is the LCP; the video is attached only after the page has loaded.
    const kick = () => setTimeout(() => { ready = true; start(); }, 400);
    if (document.readyState === 'complete') kick(); else window.addEventListener('load', kick, { once: true });
    return { sync() {
      if (grey) { if (motionAllowed() && onScreen) grey.play().catch(() => {}); else grey.pause(); }
      if (!motionAllowed()) { v.pause(); } else if (!started) { if (ready) start(); } else if (!touched && onScreen) v.play().catch(() => {}); } };
  })();

  /* ---------- Chapter openings rise into place as they arrive (transform only; skipped for reduced motion) ---------- */
  if ('IntersectionObserver' in window && !reduced()) {
    // Headings and the big interactive blocks only: cards and buttons keep still so their hover reads cleanly.
    const items = $$(q('.v3 .v3-head, .night-test, .crystal, .practice, .quiz, .stepper, .promise-block, .final-v3, .offer-ground'));
    const io = new IntersectionObserver(entries => entries.forEach(e => {
      if (!e.isIntersecting) return;
      e.target.classList.add(cls('in'));
      io.unobserve(e.target);
    }), { rootMargin: '0px 0px -8% 0px' });
    items.forEach((el, i) => {
      if (el.getBoundingClientRect().top < innerHeight) return; // already on screen: leave it still
      el.classList.add(cls('rise'));
      el.style.setProperty('--rise-delay', `${(i % 4) * 60}ms`);
      io.observe(el);
    });
  }

  /* ---------- First screen: the remote invites a tap until the visitor uses it ---------- */
  const remote = document.querySelector(q('.remote'));
  if (remote) {
    remote.classList.add(cls('invite'));
    $$('[data-scene-mode], #hero-scene').forEach(b => b.addEventListener('click', () => remote.classList.remove(cls('invite')), { once: true }));
  }

  /* ---------- Chapter bar: appears after the buy box, marks the chapter being read ---------- */
  const bar = $('sf-chapter-bar');
  if (bar && 'IntersectionObserver' in window) {
    const buy = $('buy');
    new IntersectionObserver(([e]) => {
      const below = e.boundingClientRect.bottom <= 1 && e.boundingClientRect.top < 0;   // edge-adjacent counts as intersecting: read the box
      bar.dataset.visible = String(below);
    }, { threshold: 0 }).observe(buy || document.body);
    const links = $$('[data-chap]', bar);
    const strip = bar.querySelector(q('.chapter-inner')) || bar;
    // The strip scrolls, not the bar: centre the current tab in the part not covered by the pause and bag buttons
    const mark = key => links.forEach(a => {
      const on = a.dataset.chap === key;
      a.toggleAttribute('aria-current', on);
      if (on && strip.scrollWidth > strip.clientWidth) {
        const visible = strip.clientWidth - 112;
        strip.scrollTo({ left: Math.max(0, a.offsetLeft - (visible - a.offsetWidth) / 2), behavior: reduced() ? 'auto' : 'smooth' });
      }
    });
    const io = new IntersectionObserver(entries => entries.forEach(e => { if (e.isIntersecting) mark(e.target.dataset.chapter); }), { rootMargin: '-45% 0px -50% 0px' });
    $$('[data-chapter]').forEach(s => io.observe(s));
  }
  // A theme has no html{scroll-behavior:smooth} of ours, so in-page links into our sections glide themselves
  // (the buy buttons, data-size-link, scroll in sf-app.js).
  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest?.('a[href^="#"]');
    if (!link || link.hasAttribute('data-size-link')) return;
    const id = decodeURIComponent(link.getAttribute('href').slice(1));
    const target = id && document.getElementById(id);
    if (!target || !target.closest('.sf')) return;
    event.preventDefault();
    target.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
    try { history.replaceState(null, '', '#' + id); } catch (_) { /* optional */ }
  });

  /* ---------- Rooms: tap a room, see it switch, get the usual size and price ---------- */
  let roomTier = 'small';
  // Drag between the room's clear and frosted frames, when both exist for that room.
  const ba = $('sf-room-ba'), baRange = $('sf-ba-range');
  const clone = $('sf-room-clone');
  function showPair(tile) {
    if (!ba) return;
    const clear = tile.dataset.baClear, frosted = tile.dataset.baFrosted;
    ba.hidden = !(clear && frosted);
    // No clear/frosted pair for this room yet: show the tile's own picture or loop, bigger.
    if (clone) {
      clone.hidden = !ba.hidden;
      clone.replaceChildren();
      if (ba.hidden) {
        const pic = tile.querySelector(q('.tile-media'))?.cloneNode(true);
        if (pic) {
          clone.append(pic);
          const v = pic.querySelector('video');
          if (v && v.dataset.src) { v.classList.remove(cls('lv')); v.src = v.dataset.src; v.muted = true; if (motionAllowed()) v.play().catch(() => {}); }
        }
      }
    }
    if (ba.hidden) return;
    const name = tile.querySelector(q('.room-name'))?.textContent || 'This room';
    Object.assign($('sf-ba-clear'), { src: clear, alt: `${name}: film clear on the left, frosted on the right as you drag` });
    $('sf-ba-frosted').src = frosted;
    baRange.value = '50';
    ba.style.setProperty('--pos', '50%');
  }
  baRange?.addEventListener('input', () => ba.style.setProperty('--pos', `${baRange.value}%`));
  const tiles = $$(q('.room-tile'));
  const firstTile = tiles.find(t => t.getAttribute('aria-pressed') === 'true');
  if (firstTile) { roomTier = firstTile.dataset.tier || roomTier; showPair(firstTile); }
  tiles.forEach(tile => tile.addEventListener('click', () => {
    showPair(tile);
    // The payoff sits above the tiles: bring it into view when the visitor taps a tile further down.
    const panel = $('sf-room-panel'), top = panel?.getBoundingClientRect().top ?? 0;
    if (panel && (top < 60 || top > innerHeight * 0.5)) panel.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
    tiles.forEach(t => t.setAttribute('aria-pressed', String(t === tile)));
    roomTier = tile.dataset.tier;
    const order = $('sf-room-order');
    if ($('sf-room-panel-name')) $('sf-room-panel-name').textContent = tile.querySelector(q('.room-name'))?.textContent || '';
    if ($('sf-room-panel-size')) $('sf-room-panel-size').textContent = tile.querySelector(q('.room-size'))?.textContent || '';
    if ($('sf-room-order-price')) $('sf-room-order-price').textContent = roomTier === 'quote' ? (order?.dataset.quoteLabel || '') : (tile.dataset.price || '');
    const v = tile.querySelector(q('video.lv'));
    if (v) { v.dataset.inview = '1'; play(v); }
  }));
  $('sf-room-order')?.addEventListener('click', () => {
    if (roomTier === 'quote') { $('get-quote')?.click(); return; }
    const input = document.querySelector(`input[name="sf-tier"][value="${roomTier}"]`);
    if (input && !input.checked) { input.checked = true; input.dispatchEvent(new Event('change', { bubbles: true })); }
    const head = $('path-later-head');
    if (head && head.getAttribute('aria-expanded') !== 'true') head.click();
    document.querySelector(q('.path[data-path="later"]'))?.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
    live(`${roomTier === 'small' ? 'Small' : 'Medium'} selected. Measure later and send us your size within 14 days.`);
  });

  /* ---------- Night test: film clear or frosted, lamps on ---------- */
  const night = $('sf-night-test');
  if (night) {
    $$('[data-night]', night).forEach(b => b.addEventListener('click', () => {
      night.dataset.film = b.dataset.night;
      night.dataset.touched = '1';
      $$('[data-night]', night).forEach(x => x.setAttribute('aria-pressed', String(x === b)));
      if ($('sf-night-caption') && b.dataset.caption) $('sf-night-caption').textContent = b.dataset.caption;
      const v = night.querySelector(q('video.lv'));
      if (v) { v.dataset.hold = '1'; pause(v); }
    }));
  }

  /* ---------- Card rails (old fixes): previous / next ---------- */
  $$(q('.rail-btn')).forEach(b => b.addEventListener('click', () => {
    const rail = $(b.dataset.rail);
    const card = rail?.firstElementChild;
    if (rail && card) rail.scrollBy({ left: Number(b.dataset.dir) * (card.getBoundingClientRect().width + 14), behavior: reduced() ? 'auto' : 'smooth' });
  }));
  $$('a[data-card]').forEach(a => a.addEventListener('click', () => {
    const card = document.querySelector(q(`.fix-card[data-card="${a.dataset.card}"]`));
    setTimeout(() => card?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: reduced() ? 'auto' : 'smooth' }), 350);
  }));

  /* ---------- Power on / off / unplugged ---------- */
  const crystal = $('sf-crystal');
  if (crystal) {
    $$('[data-power]', crystal).forEach(b => b.addEventListener('click', () => {
      crystal.dataset.power = b.dataset.power;
      $$('[data-power]', crystal).forEach(x => x.setAttribute('aria-pressed', String(x === b)));
      if ($('sf-crystal-caption') && b.dataset.caption) $('sf-crystal-caption').textContent = b.dataset.caption;
    }));
  }

  /* ---------- Running cost on the visitor's own rate (supplier's 6 W/m² upper bound; no default rate) ---------- */
  const cost = { hours: $('sf-cost-hours'), area: $('sf-cost-area'), rate: $('sf-cost-rate') };
  let areaTouched = false;
  function updateCost() {
    if (!cost.hours || !cost.area) return;
    const hours = Number(cost.hours.value);
    const sqft = Number(cost.area.value);
    if ($('sf-cost-hours-out')) $('sf-cost-hours-out').textContent = String(hours);
    if ($('sf-cost-area-out')) $('sf-cost-area-out').textContent = `${sqft} sq ft`;
    const m2 = sqft * 0.092903;
    const kwh = m2 * (config.energy?.wattsPerM2 || 6) * hours * 30 / 1000;
    const rate = parseFloat(String(cost.rate?.value || '').replace(/[^0-9.]/g, ''));
    const kwhText = kwh < 0.1 ? 'Under 0.1 kWh a month' : `About ${kwh.toFixed(1)} kWh a month`;
    const priced = Number.isFinite(rate) && rate > 0 && rate < 5;
    if ($('sf-cost-month')) $('sf-cost-month').textContent = priced
      ? `About ${kwh * rate < 1 ? Math.max(1, Math.round(kwh * rate * 100)) + '¢' : '$' + (kwh * rate).toFixed(2)} a month` : kwhText;
    if ($('sf-cost-basis')) $('sf-cost-basis').textContent = priced ? `${kwhText.replace('About ', '').toLowerCase()} at your rate, for this window` : 'for this window. Add your rate to see dollars.';
  }
  cost.hours?.addEventListener('input', updateCost);
  cost.area?.addEventListener('input', () => { areaTouched = true; updateCost(); });
  cost.rate?.addEventListener('input', updateCost);
  // Follow the size typed in the buy box until the visitor moves a slider.
  function boxArea() {
    const w = parseFloat($('width')?.value), h = parseFloat($('height')?.value);
    if (!(w > 0 && h > 0)) return null;
    const cm = $('unit-cm')?.getAttribute('aria-pressed') === 'true';
    return cm ? (w * h) / 929.0304 : (w * h) / 144;
  }
  ['width', 'height'].forEach(id => $(id)?.addEventListener('input', () => {
    const sqft = boxArea();
    if (sqft && !areaTouched && cost.area) { cost.area.value = String(Math.min(40, Math.max(2, Math.round(sqft)))); updateCost(); }
    if (sqft && gap.size && !gapTouched) { gap.size.value = String(Math.min(30, Math.max(4, Math.round(sqft)))); updateGap(); }
  }));
  updateCost();

  /* ---------- Two hotels first, the others on request ---------- */
  $('sf-more-hotels')?.addEventListener('click', e => {
    $('sf-hotel-cards')?.setAttribute('data-all', '');
    e.currentTarget.hidden = true;
    $('sf-hotel-cards')?.querySelector('li:nth-child(3) a')?.focus({ preventScroll: true });
  });

  /* ---------- Your window, two ways (the product's own price steps vs installed quotes) ---------- */
  const gap = { size: $('sf-gap-size') };
  let gapTouched = false;
  function kitCents(sqft) {
    const variants = config.variants || [];
    if (!variants.length) return null;
    const base = Number(config.baseAreaM2) || 0.75, step = Number(config.stepAreaM2) || 0.1;
    const m2 = sqft * 0.092903;
    const index = Math.max(0, Math.ceil((m2 - base) / step - 1e-9));
    const variant = variants[Math.min(index, variants.length - 1)];
    return variant ? variant.price : null;
  }
  function updateGap() {
    if (!gap.size) return;
    const sqft = Number(gap.size.value);
    if ($('sf-gap-size-out')) $('sf-gap-size-out').textContent = `${sqft} sq ft`;
    const cents = kitCents(sqft);
    if (cents !== null && $('sf-gap-kit')) $('sf-gap-kit').textContent = money(cents);
    const lo = Math.round(sqft * 70 / 10) * 10, hi = Math.round(sqft * 150 / 10) * 10;
    if ($('sf-gap-pro')) $('sf-gap-pro').textContent = `about ${usd(lo)}–${usd(hi).slice(1)}`;
  }
  gap.size?.addEventListener('input', () => { gapTouched = true; updateGap(); });
  updateGap();

  /* ---------- Practice measuring: tap six lines, keep the smallest ---------- */
  const practice = $('sf-practice');
  if (practice) {
    const lines = $$(q('.pw-line'), practice);
    const frac = v => {
      const whole = Math.floor(v), f = Math.round((v - whole) * 8);
      return f === 0 ? `${whole}"` : `${whole} ${['', '⅛', '¼', '⅜', '½', '⅝', '¾', '⅞'][f]}"`;
    };
    const result = $('sf-practice-result');
    const measure = g => {
      if (g.dataset.done) return;
      g.dataset.done = '1';
      g.classList.add(cls('done'));
      g.querySelector('text').textContent = frac(Number(g.dataset.val));
      const done = lines.filter(l => l.dataset.done);
      if (done.length < lines.length) { if (result) result.textContent = `${done.length} of 6 measured.`; return; }
      const pick = axis => lines.filter(l => l.dataset.axis === axis).reduce((a, b) => (Number(b.dataset.val) < Number(a.dataset.val) ? b : a));
      const w = pick('w'), h = pick('h');
      w.classList.add(cls('min')); h.classList.add(cls('min'));
      if (result) result.textContent = `Smallest width: ${frac(Number(w.dataset.val))}. Smallest height: ${frac(Number(h.dataset.val))}. Those are your two numbers.`;
    };
    lines.forEach(g => {
      g.addEventListener('click', () => measure(g));
      g.addEventListener('keydown', e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); measure(g); } });
    });
  }

  /* ---------- Will it work on my window? The verdicts are in the page (editable); this picks one ---------- */
  const quiz = $('sf-quiz');
  if (quiz) {
    const answers = {};
    const result = $('sf-quiz-result');
    function verdict() {
      if (!result || !answers.flat || !answers.rect || !answers.socket) return;
      let key;
      if (answers.flat === 'no') key = 'flat-no';
      else if (answers.rect === 'no') key = 'rect-no';
      else if ([answers.flat, answers.rect, answers.socket].includes('unsure')) key = 'unsure';
      else if (answers.shower === 'yes') key = 'shower';
      else if (answers.socket === 'no') key = 'socket-no';
      else key = 'good';
      $$('[data-verdict]', result).forEach(v => { v.hidden = v.dataset.verdict !== key; });
      result.hidden = false;
    }
    $$('[data-q]', quiz).forEach(li => $$('[data-a]', li).forEach(b => b.addEventListener('click', () => {
      answers[li.dataset.q] = b.dataset.a;
      $$('[data-a]', li).forEach(x => x.setAttribute('aria-pressed', String(x === b)));
      verdict();
    })));
  }

  /* ---------- Method chips that open the existing camera tool ---------- */
  $$('[data-open-camera]').forEach(b => b.addEventListener('click', () => $('open-camera-measure')?.click()));

  /* ---------- Drag through the install ---------- */
  const stepper = $('sf-stepper');
  if (stepper) {
    const range = $('sf-stepper-range');
    const pane = n => (n <= 2 ? 1 : n);
    function setStep(n) {
      stepper.dataset.step = String(n);
      if (range) range.value = String(n);
      $$(q('.stepper-pane'), stepper).forEach(p => { p.hidden = Number(p.dataset.pane) !== pane(n); });
      $$(q('.stepper-steps li'), stepper).forEach(li => li.querySelector('button')?.setAttribute('aria-pressed', String(Number(li.dataset.step) === n)));
      if ($('sf-stepper-done')) $('sf-stepper-done').hidden = n !== 4;
      const v = stepper.querySelector(q(`.stepper-pane[data-pane="${pane(n)}"] video.lv`));
      if (v) { v.dataset.inview = '1'; play(v); }
    }
    range?.addEventListener('input', () => setStep(Number(range.value)));
    $$(q('.stepper-steps li'), stepper).forEach(li => li.querySelector('button')?.addEventListener('click', () => setStep(Number(li.dataset.step))));
    setStep(1);
  }

  /* ---------- Specs: six cards first, the rest on request ---------- */
  $('sf-specs-more')?.addEventListener('click', () => {
    const cards = $('sf-specs-cards');
    if (!cards) return;
    cards.dataset.all = '1';
    const seventh = cards.querySelector(q('.spec:nth-child(7) dt'));
    seventh?.setAttribute('tabindex', '-1');
    seventh?.focus({ preventScroll: true });
  });

  /* ---------- Compare the kit with one other way (phones) ---------- */
  const table = $('sf-cmp-table');
  if (table) {
    const pick = n => {
      table.dataset.pick = String(n);
      $$(q('.cmp-pick [data-pick]')).forEach(b => b.setAttribute('aria-pressed', String(b.dataset.pick === String(n))));
    };
    $$(q('.cmp-pick [data-pick]')).forEach(b => b.addEventListener('click', () => { pick(Number(b.dataset.pick)); markSame(); }));
    // On phones, hide the rows where the kit and the chosen option score the same.
    const value = td => td?.querySelector('[aria-label]')?.getAttribute('aria-label') || td?.textContent.trim() || '';
    function markSame() {
      const n = table.dataset.pick;
      $$('tbody tr', table).forEach(tr => tr.classList.toggle(cls('same'), value(tr.querySelector('[data-col="0"]')) === value(tr.querySelector(`[data-col="${n}"]`))));
    }
    $('sf-cmp-diff')?.addEventListener('click', e => {
      const on = e.currentTarget.getAttribute('aria-pressed') !== 'true';
      e.currentTarget.setAttribute('aria-pressed', String(on));
      table.toggleAttribute('data-diff', on);
      markSame();
    });
    pick(1);
    markSame();
  }

  /* ---------- What's in the box: each line lights its part on the kit photo ---------- */
  const kit = $('sf-kit-figure');
  if (kit) {
    const hot = $$('[data-hot-btn]');
    const light = n => {
      $$(q('.hotspot'), kit).forEach(h => h.classList.toggle(cls('on'), h.dataset.hot === n));
      if (n) kit.dataset.active = n; else delete kit.dataset.active;
    };
    // Hover previews a part; a click pins it (and a second click unpins), so clicking a hovered line keeps it lit.
    let pinned = '';
    const pin = n => {
      pinned = n;
      hot.forEach(b => b.setAttribute('aria-pressed', String(b.dataset.hotBtn === n)));
      light(n);
    };
    hot.forEach(b => {
      b.setAttribute('aria-pressed', 'false');
      b.addEventListener('click', () => pin(pinned === b.dataset.hotBtn ? '' : b.dataset.hotBtn));
      b.addEventListener('mouseenter', () => light(b.dataset.hotBtn));
      b.addEventListener('mouseleave', () => light(pinned));
    });
  }

  /* ---------- Send this to someone at home ---------- */
  $('sf-share-page')?.addEventListener('click', async event => {
    const button = event.currentTarget;
    const data = { title: button.dataset.shareTitle || document.title, text: button.dataset.shareText || '', url: location.href.split('#')[0] };
    try {
      if (navigator.share) { await navigator.share(data); return; }
      await navigator.clipboard.writeText(`${data.text} ${data.url}`);
      live('Link copied. Paste it into a message.');
    } catch (_) { live('Copy this page address from your browser to share it.'); }
  });

  /* ---------- S5 · Blender mechanism loop: the chip of the beat on screen lights up ---------- */
  $$('[data-chip-starts]').forEach(list => {
    const v = list.previousElementSibling?.querySelector('video');
    const beats = list.dataset.chipStarts.split(',').map((p, i) => { const [t, c] = p.split(':'); return [Number(t), c === undefined ? i : Number(c)]; });
    const items = [...list.children], on = cls('on');
    const set = time => { let k = 0; beats.forEach(([s, c]) => { if (time >= s) k = c; }); items.forEach((li, i) => li.classList.toggle(on, i === k)); list.style.setProperty('--chip-p', `${(k + 1) / items.length * 100}%`); };
    set(0);
    if (!v) return;
    v.addEventListener('timeupdate', () => set(v.currentTime));
    if ('requestVideoFrameCallback' in v) { const tick = (_, meta) => { set(meta.mediaTime); v.requestVideoFrameCallback(tick); }; v.requestVideoFrameCallback(tick); }
  });

  /* ---------- Buy box · ship-by date: today + the longest made-to-order lead time (3 weeks), never a made-up deadline ---------- */
  $$('[data-ship-by]').forEach(el => {
    const out = el.querySelector('[data-ship-date]');
    if (!out) return;
    out.textContent = new Date(Date.now() + 21 * 864e5).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    el.hidden = false;
  });

  /* ---------- The Returns link next to the buy button opens the right FAQ answer ---------- */
  $$(`[data-faq-link], ${q('.buy-assure')} a[href^="#faq-"]`).forEach(a => a.addEventListener('click', () => {
    const href = a.getAttribute('href') || '';
    const group = a.dataset.faqLink || (href.match(/^#faq-(.+)-\d+$/) || [])[1];
    if (group) document.querySelector(`[data-faq-group="${group}"]`)?.click();
    const target = href.startsWith('#') && href.length > 1 ? document.querySelector(href) : null;
    if (target && target.tagName === 'DETAILS') target.open = true;
  }));

  /* ---------- Skim round: a picture pair crossfades on its own; a tap holds the other state, the next tap swaps ---------- */
  $$('.' + cls('pair')).forEach(pair => {
    const btn = pair.querySelector('.' + cls('pair-btn')), b = pair.querySelector('.' + cls('pair-b'));
    btn?.addEventListener('click', () => {
      const showing = pair.classList.contains(cls('is-held')) ? pair.dataset.state : (b && parseFloat(getComputedStyle(b).opacity) > 0.5 ? 'frosted' : 'clear');
      const next = showing === 'frosted' ? 'clear' : 'frosted';
      pair.classList.add(cls('is-held'));
      pair.dataset.state = next;
      btn.setAttribute('aria-pressed', String(next === 'frosted'));
    });
  });

  setPaused(paused);
})();

(() => {
  /* ---------- Proof videos play here, not on YouTube (owner 30/10: a shopper who leaves the page forgets the purchase) ---------- */
  const ytId = href => { const m = String(href).match(/(?:v=|shorts\/|youtu\.be\/|embed\/)([\w-]{11})/); return m && m[1]; };
  document.addEventListener('click', e => {
    const a = e.target.closest('a[href*="youtube.com/"], a[href*="youtu.be/"]');
    const dialog = document.getElementById('video-dialog'), frame = document.getElementById('source-video');
    const id = a && (a.dataset.yt || ytId(a.href));
    if (!id || !dialog || !frame) return;
    e.preventDefault();
    frame.src = `https://www.youtube-nocookie.com/embed/${id}?rel=0&autoplay=1&playsinline=1`;
    const note = document.getElementById('video-note');
    if (note) note.textContent = a.getAttribute('aria-label') || a.textContent.trim();
    if (typeof dialog.showModal === 'function' && !dialog.open) dialog.showModal();
  });
})();
